/**
 * Chatbot Store - State management for onboarding assistant
 *
 * Manages chat state, messages, and conversation history
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ChatMessage {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
  quickActions?: QuickAction[];
  relatedTopics?: string[];
}

export interface QuickAction {
  id: string;
  label: string;
  action: string;
  icon?: string;
}

export interface ChatbotState {
  // UI State
  isOpen: boolean;
  isMinimized: boolean;
  hasUnread: boolean;

  // Messages
  messages: ChatMessage[];
  isTyping: boolean;

  // User context
  onboardingStep: number;
  visitedTopics: string[];
  lastInteraction: Date | null;

  // Actions
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  minimizeChat: () => void;
  maximizeChat: () => void;

  addUserMessage: (content: string) => void;
  addBotMessage: (
    content: string,
    options?: { quickActions?: QuickAction[]; relatedTopics?: string[] }
  ) => void;
  setTyping: (isTyping: boolean) => void;
  clearMessages: () => void;

  markTopicVisited: (topicId: string) => void;
  setOnboardingStep: (step: number) => void;
  markAsRead: () => void;
}

export const useChatbotStore = create<ChatbotState>()(
  persist(
    (set, get) => ({
      // Initial UI State
      isOpen: false,
      isMinimized: false,
      hasUnread: false,

      // Initial Messages
      messages: [],
      isTyping: false,

      // Initial User Context
      onboardingStep: 0,
      visitedTopics: [],
      lastInteraction: null,

      // UI Actions
      openChat: () => {
        const state = get();
        // Add welcome message if first time
        if (state.messages.length === 0) {
          set({
            isOpen: true,
            isMinimized: false,
            hasUnread: false,
            messages: [
              {
                id: 'welcome-1',
                type: 'bot',
                content:
                  'Cześć! Jestem Twoim asystentem SEO Intelligence Platform. Mogę pomóc Ci w:',
                timestamp: new Date(),
                quickActions: [
                  { id: 'start', label: 'Rozpoczęcie pracy', action: 'topic:getting-started' },
                  {
                    id: 'profile',
                    label: 'Konfiguracja profilu',
                    action: 'topic:company-profile',
                  },
                  { id: 'integrations', label: 'Integracje Google', action: 'topic:gsc' },
                  { id: 'analysis', label: 'Analiza SEO', action: 'topic:keyword-research' },
                ],
              },
            ],
            lastInteraction: new Date(),
          });
        } else {
          set({ isOpen: true, isMinimized: false, hasUnread: false });
        }
      },

      closeChat: () => set({ isOpen: false }),

      toggleChat: () => {
        const state = get();
        if (state.isOpen) {
          set({ isOpen: false });
        } else {
          get().openChat();
        }
      },

      minimizeChat: () => set({ isMinimized: true }),

      maximizeChat: () => set({ isMinimized: false }),

      // Message Actions
      addUserMessage: (content: string) => {
        const newMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          type: 'user',
          content,
          timestamp: new Date(),
        };

        set((state) => ({
          messages: [...state.messages, newMessage],
          lastInteraction: new Date(),
        }));
      },

      addBotMessage: (content, options) => {
        const newMessage: ChatMessage = {
          id: `bot-${Date.now()}`,
          type: 'bot',
          content,
          timestamp: new Date(),
          quickActions: options?.quickActions,
          relatedTopics: options?.relatedTopics,
        };

        set((state) => ({
          messages: [...state.messages, newMessage],
          isTyping: false,
          hasUnread: !state.isOpen,
          lastInteraction: new Date(),
        }));
      },

      setTyping: (isTyping: boolean) => set({ isTyping }),

      clearMessages: () =>
        set({
          messages: [],
          onboardingStep: 0,
        }),

      // Context Actions
      markTopicVisited: (topicId: string) =>
        set((state) => ({
          visitedTopics: state.visitedTopics.includes(topicId)
            ? state.visitedTopics
            : [...state.visitedTopics, topicId],
        })),

      setOnboardingStep: (step: number) => set({ onboardingStep: step }),

      markAsRead: () => set({ hasUnread: false }),
    }),
    {
      name: 'chatbot-storage',
      partialize: (state) => ({
        messages: state.messages.slice(-50), // Keep last 50 messages
        onboardingStep: state.onboardingStep,
        visitedTopics: state.visitedTopics,
        lastInteraction: state.lastInteraction,
      }),
    }
  )
);
